import { useCallback, useEffect, useState } from 'react';

// Watchdog timeout — unmounts the element if `transitionend` doesn't fire
// within this window after `isVisible` flips false. Comfortably longer than
// any opacity transition we use (≤500ms) so it only kicks in when the event
// is missed, not for normal fades.
const TRANSITION_END_WATCHDOG_MS = 1000;

/**
 * Coordinates the mount/unmount lifecycle of an element that fades out via a
 * CSS opacity transition. Returns a flag for whether the element should be in
 * the DOM, plus an `onTransitionEnd` handler that unmounts it once the fade
 * completes.
 *
 * Why this exists — skeleton/loader elements typically use an
 * `animation: ... infinite` keyframe rule. Leaving them mounted (even at
 * opacity 0) keeps the animation running, which produces style recalcs and
 * paints every frame for the lifetime of the page. Conditionally rendering
 * on the visibility flag alone would skip the fade, since the element would
 * unmount before its opacity transition could run. This hook bridges the gap.
 *
 * Initial mount: `isMounted` starts equal to `isVisible`, so an element that
 * is hidden on first render never mounts at all — there would be no opacity
 * change to trigger `transitionend`, and the element would otherwise be stuck
 * in the DOM forever.
 *
 * The handler filters to `propertyName === 'opacity'`, since `transitionend`
 * bubbles and fires per-property — without this filter, a sibling/child with
 * its own transition (e.g. `transform`) could spuriously trigger unmount.
 *
 * Watchdog: if the opacity transition is interrupted (e.g. an ancestor toggles
 * `display: none` mid-fade), the browser fires `transitioncancel` instead of
 * `transitionend`, which React does not bridge to `onTransitionEnd`. Without
 * a fallback, the skeleton would stay mounted indefinitely. The watchdog
 * unmounts after the transition would have completed regardless of whether
 * the event fired.
 */
export default function useFadeOutUnmount(isVisible: boolean) {
  const [isMounted, setIsMounted] = useState(isVisible);

  useEffect(() => {
    if (isVisible) {
      setIsMounted(true);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setIsMounted(false);
    }, TRANSITION_END_WATCHDOG_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isVisible]);

  const onTransitionEnd = useCallback(
    (event: React.TransitionEvent) => {
      if (event.propertyName !== 'opacity') {
        return;
      }
      if (!isVisible) {
        setIsMounted(false);
      }
    },
    [isVisible]
  );

  return { isMounted, onTransitionEnd };
}
